import { useQuery, queryOptions } from '@tanstack/react-query';
import { apiClient } from '../client';
import { isNotFoundError } from '../errors';
import { authKeys } from '../query-keys';
import { teardownSessionToLogin } from '@/lib/auth/sessionTeardown';
import { useAuthStore } from '@/stores/auth-store';
import { profileContract, type User, type ProfileResponse } from '@bliks-io/contracts';

/**
 * Standalone query options for profile data.
 * Use with `queryClient.ensureQueryData(profileQueryOptions)` in route guards.
 */
export const profileQueryOptions = queryOptions({
  queryKey: authKeys.profile(),
  queryFn: async ({ signal }): Promise<ProfileResponse> => {
    try {
      const { data } = await apiClient.get<ProfileResponse>(profileContract.path, { signal });
      return data;
    } catch (error) {
      if (isNotFoundError(error)) {
        // Deleted account: full teardown, not just clearAuth — the cached
        // profile would otherwise authorize the NEXT login's guards.
        teardownSessionToLogin();
      }
      throw error;
    }
  }
});

/**
 * Fetch current user profile (full response including permissions and authContext).
 * Shared query — all profile-related hooks read from this cache entry.
 *
 * `options` is spread AFTER `enabled`, so a caller can add refetch behaviour
 * (the seat-overrun lock page polls) without re-spelling the token gate.
 * Spelled out rather than picked off `UseQueryOptions`: its function forms
 * carry a `queryKey` generic that clashes with the shared `queryOptions()`
 * literal key.
 */
function useProfileQuery(options?: {
  refetchInterval?: number;
  refetchOnWindowFocus?: boolean | 'always';
}) {
  const { token } = useAuthStore();

  return useQuery({
    ...profileQueryOptions,
    enabled: !!token,
    ...options
  });
}

/**
 * Fetch current user profile
 *
 * Automatically enabled when user is authenticated (has token).
 * The profile data can be pre-populated from sign-in response to avoid redundant API calls.
 *
 * @example
 * ```tsx
 * function UserMenu() {
 *   const { data: user, isLoading } = useProfile();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!user) return null;
 *
 *   return <Avatar src={getUserAvatarUrl(user)} />;
 * }
 * ```
 */
export function useProfile() {
  const query = useProfileQuery();

  return {
    ...query,
    data: query.data?.data as User | undefined
  };
}

export { useProfileQuery };
